/**
 * Analytics View Component
 *
 * Main orchestrator for statistical analysis and summaries
 */

import React from 'react'
import { useSyncedStore } from '@syncedstore/react'
import { todoStore } from 'src/store/todo'
import type { Worker } from 'src/store/todo/types'
import { useAnalyticsData } from '../../hooks'
import { checkForDuplicates } from 'src/utils/duplicates'
import HarvestSection from './harvest-section'
import TaskSection from './task-section'

type AnalyticsViewProps = {
  /** Currently selected team ID */
  selectedTeamId: string
  /** Callback when worker summary is requested - now shows ALL harvests */
  onShowWorkerSummary: (workerId: string, workerName: string, harvestTaskIds: string[]) => void
}

/**
 * AnalyticsView component showing statistical summaries
 *
 * @example
 * <AnalyticsView
 *   selectedTeamId={teamId}
 *   onShowWorkerSummary={handleShowSummary}
 * />
 */
const AnalyticsView: React.FC<AnalyticsViewProps> = ({
  selectedTeamId,
  onShowWorkerSummary,
}) => {
  const state = useSyncedStore(todoStore)

  // Get filtered workers
  const workers =
    (state.workers?.filter((w: any) => w.teamId === selectedTeamId && w.isActive) as Worker[]) ||
    []

  // Check for duplicates and warn
  React.useEffect(() => {
    checkForDuplicates(workers, 'Workers')
  }, [workers.length])

  // Get analytics data using custom hook
  const { getHarvestAnalytics, getNonHarvestAnalytics } = useAnalyticsData(
    selectedTeamId,
    workers
  )

  const harvestData = getHarvestAnalytics()
  const nonHarvestData = getNonHarvestAnalytics()

  return (
    <div className="flex flex-col gap-3 py-4 pb-32">
      {/* Harvest Workers Section */}
      <HarvestSection
        hvstTasks={harvestData.hvstTasks}
        teamTotalBoxes={harvestData.teamTotalBoxes}
        formattedTotal={harvestData.formattedTotal}
        harvestWorkers={harvestData.harvestWorkers}
        onShowWorkerSummary={onShowWorkerSummary}
      />

      {/* Non-Harvest Tasks Sections */}
      {nonHarvestData.map((data) => (
        <TaskSection
          key={data.task.id}
          task={data.task}
          taskTotalCompleted={data.taskTotalCompleted}
          workersForTask={data.workersForTask}
        />
      ))}
    </div>
  )
}

export default AnalyticsView
